
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";



// 引入 store
const management = namespace("management");
const monitor = namespace("monitor");


@Component
class StoreMonitor extends Vue {
  // 门店选择相关
  selectedStoreCode: string = "";
  storeList: any[] = [];
  isSearching: boolean = false;
  searchKeyword: string = "";
  isRefreshing: boolean = false;
  isFullScreen: boolean = false;

  // 自动滚动相关
  autoScrollTimers: any[] = [];
  scrollSpeed: number = 1;
  scrollInterval: number = 50; // 默认滚动间隔，单位毫秒
  isAutoScrollEnabled: boolean = false; // 默认不开启自动滚动

  monitorData: any = {
    todayAppointment: 0,
    todayArrival: 0,
    inFactory: 0,
    todayDeparture: 0,
    workStationList: [],
    waitingQueue: [],
    timeoutQueue: []
  };
  @management.Action getStoreList;

  @monitor.Action storeMonitor;

  async mounted() {
    await this.initStoreList();
    await this.initData();
    // 监听全屏状态变化
    document.addEventListener('fullscreenchange', this.handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullScreenChange);

    // 默认不启动自动滚动，需要用户手动开启
  }

  beforeDestroy() {
    // 移除全屏状态变化监听
    document.removeEventListener('fullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullScreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullScreenChange);

    // 停止自动滚动
    this.stopAutoScroll();
  }

  // 启动自动滚动
  startAutoScroll() {
    if (!this.isAutoScrollEnabled) {
      return;
    }

    // 为工位施工情况设置自动滚动
    this.setupAutoScroll('.station-scroll-container');

    // 为待进店队列和预约未到设置自动滚动
    const queueContainers = document.querySelectorAll('.queue-scroll-area');
    queueContainers.forEach((container) => {
      this.setupAutoScroll(container);
    });
  }

  // 停止自动滚动
  stopAutoScroll() {
    this.autoScrollTimers.forEach(timer => {
      clearInterval(timer);
    });
    this.autoScrollTimers = [];
  }

  // 切换自动滚动状态
  toggleAutoScroll() {
    this.isAutoScrollEnabled = !this.isAutoScrollEnabled;

    if (this.isAutoScrollEnabled) {
      // 开启自动滚动
      this.startAutoScroll();
    } else {
      // 关闭自动滚动
      this.stopAutoScroll();
    }
  }

  // 为特定容器设置自动滚动
  setupAutoScroll(container) {
    const scrollContainer = typeof container === 'string' ? document.querySelector(container) : container;

    if (!scrollContainer) {
      return;
    }

    // 设置一个定时器，实现自动滚动
    const timer = setInterval(() => {
      // 滚动速度
      scrollContainer.scrollTop += this.scrollSpeed;

      // 检查是否滚动到底部
      if (scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight) {
        // 滚动到顶部，重新开始
        scrollContainer.scrollTop = 0;
      }
    }, this.scrollInterval); // 使用自定义的滚动间隔

    this.autoScrollTimers.push(timer);
  }

  // 监听滚动间隔变化
  @Watch('scrollInterval')
  onScrollIntervalChange() {
    if (this.isAutoScrollEnabled) {
      // 停止当前的自动滚动
      this.stopAutoScroll();
      // 重新启动自动滚动，应用新的滚动间隔
      this.startAutoScroll();
    }
  }

  // 切换全屏状态
  toggleFullScreen() {
    // 找到当前页面的容器元素
    const element = document.querySelector('.store-monitor-container');

    if (!element) {
      console.error('找不到页面容器元素');
      return;
    }

    if (!this.isFullScreen) {
      // 进入全屏
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        (element as any).msRequestFullscreen();
      }
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }

  // 处理全屏状态变化
  handleFullScreenChange() {
    this.isFullScreen = !!(document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement);
  }

  // 防抖函数
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // 防抖后的搜索方法
  debouncedSearch = this.debounce(this.remoteSearchStoreInternal, 300);

  handleStoreChange(value) {
    // 当门店选择改变时，重新加载数据
    this.refreshData();
  }

  // 远程搜索门店
  remoteSearchStore(query) {
    this.searchKeyword = query;
    this.debouncedSearch(query);
  }

  // 实际执行搜索的方法
  async remoteSearchStoreInternal(query) {
    if (!query) {
      // 如果搜索关键词为空，重新加载所有门店
      await this.initStoreList();
      return;
    }

    this.isSearching = true;
    try {
      const res = await this.getStoreList({
        pageNum: 1,
        pageSize: 100,
        storeName: query // 假设接口支持通过storeName参数进行模糊搜索
      });

      if (res && res.data && res.data.list) {
        this.storeList = res.data.list.map(item => {
          return {
            label: item.storeName,
            value: item.storeCode
          };
        });
      }
    } catch (error) {
      console.error('搜索门店失败:', error);
      this.$message.error('搜索门店失败');
    } finally {
      this.isSearching = false;
    }
  }

  async initStoreList(searchKeyword = '') {
    try {
      const res = await this.getStoreList({
        pageNum: 1,
        pageSize: 100,
        storeName: searchKeyword,
        storeTypeList: ['3'],
      });
      if (res && res.data && res.data.list) {
        this.storeList = res.data.list.map(item => {
          return {
            label: item.storeName,
            value: item.storeCode
          };
        });
        this.selectedStoreCode = this.storeList[0].value;
      }
    } catch (error) {
      console.error('获取门店列表失败:', error);
      this.$message.error('获取门店列表失败');
    }
  }

  async initData() {
    // 这里应该调用接口获取实际数据
    // 暂时使用模拟数据
    console.log('初始化门店监控数据');

    const res = await this.storeMonitor({
      storeCode: this.selectedStoreCode
    });
    console.log(res);

    if (res && res.data) {
      this.monitorData = res.data;
    }
  }

  async refreshData() {
    // 开始刷新动画
    this.isRefreshing = true;

    try {
      // 刷新所有数据
      await this.initData();
      this.$message.success("数据已刷新");
    } catch (error) {
      console.error('刷新数据失败:', error);
      this.$message.error('刷新数据失败');
    } finally {
      // 结束刷新动画
      setTimeout(() => {
        this.isRefreshing = false;
      }, 500);
    }
  }

  // 获取状态标签类型
  getStatusType(status: string): string {
    switch (status) {
      case "施工中":
        return "warning";
      case "空闲":
        return "info";
      case "待确认":
        return "info";
      case "已确认":
        return "success";
      case "超时未到":
        return "danger";
      default:
        return "default";
    }
  }

  // 获取进度条状态
  getProgressStatus(progress: number): string | undefined {
    if (progress >= 100) {
      return "success";
    }
    return undefined;
  }

  // 表格行样式类名
  tableRowClassName({ row }: { row: any }): string {
    switch (row.appointmentStatus) {
      case "待确认":
        return "status-pending";
      case "已确认":
        return "status-confirmed";
      case "超时未到":
        return "status-timeout";
      default:
        return "";
    }
  }
}

export default StoreMonitor;
